/**
 * Flow routing (SPEC §3.2): snap a point to a river reach, trace downstream to
 * the outlet (or a destination), and estimate travel time as a RANGE.
 *
 * Uncalibrated (SPEC §3.4 pending) — velocity uses v = a·Q^b with a plausible
 * parameter band, so ETA is reported as fast/slow bounds, never a single value.
 */
export const GULF = "gulf";
// Velocity law v = a·Q^b, clamped to [0.2, 3] m/s (SPEC §3.2.4).
// aNominal calibrated against the 2564 (2021) Chao Phraya flood: the C.2 (Nakhon
// Sawan) → C.13 (Chao Phraya Dam) peak lag (GloFAS cross-correlation, 1 day over
// ~99 km) fits a ≈ 0.236 (model error ~0%). aFast/aSlow bracket flood vs low-flow
// conditions + parameter uncertainty, so ETA is always a range (SPEC §3.4).
export const VELOCITY = { b: 0.2, aNominal: 0.236, aFast: 0.34, aSlow: 0.14, vMin: 0.2, vMax: 3 };
/** Calibration provenance (shown in UI / API; ETA stays a range regardless). */
export const CALIBRATION = {
    calibrated: true,
    partial: true,
    basis: "2564 เจ้าพระยา (C.2→C.13, GloFAS cross-correlation, error ~0%)",
    note: "ปรับเทียบเบื้องต้นเฉพาะลุ่มเจ้าพระยา · ลุ่มอื่นยังรอข้อมูลสถานีจริง (กรมชลประทาน/สสน.)",
};
const EARTH_R_KM = 6371;
const DEG = Math.PI / 180;
export function haversineKm(lon1, lat1, lon2, lat2) {
    const dLat = (lat2 - lat1) * DEG;
    const dLon = (lon2 - lon1) * DEG;
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLon / 2) ** 2;
    return EARTH_R_KM * 2 * Math.asin(Math.min(1, Math.sqrt(a)));
}
/** Nearest reach snap point within `maxKm` (linear scan — snap.json is ~24k pts). */
export function snap(points, lon, lat, maxKm = 5) {
    let best = null;
    for (const p of points) {
        const d = haversineKm(lon, lat, p[0], p[1]);
        if (best === null || d < best.distKm)
            best = { id: p[2], lon: p[0], lat: p[1], distKm: d };
    }
    return best && best.distKm <= maxKm ? best : null;
}
/**
 * v0.9.9 C1 — nearest snap point on a MAIN river (Strahler order ≥ minOrder, graph[id][3]) within
 * `maxKm`: the "แม่น้ำใกล้คุณ" of the summary (D1), the 〰 chip and the 📍 label share it.
 * (snap() above = the nearest reach of any size — where the user's water starts its route.)
 */
export function snapMain(points, graph, lon, lat, maxKm, minOrder = 5) {
    let best = null;
    for (const p of points) {
        const t = graph[String(p[2])];
        if (!t || (t[3] ?? 0) < minOrder)
            continue;
        const d = haversineKm(lon, lat, p[0], p[1]);
        if (best === null || d < best.distKm)
            best = { id: p[2], lon: p[0], lat: p[1], distKm: d };
    }
    return best && best.distKm <= maxKm ? best : null;
}
export function alongRiverKm(riverKm, lon, lat, maxKm) {
    const kx = 111.32 * Math.cos((lat * Math.PI) / 180), ky = 110.57; // local km per degree
    let best = null, bestD2 = maxKm * maxKm;
    for (const id in riverKm) {
        const v = riverKm[id];
        for (let i = 0; i + 1 < v.length; i++) {
            const a = v[i], b = v[i + 1];
            const ax = (a[0] - lon) * kx, ay = (a[1] - lat) * ky, bx = (b[0] - lon) * kx, by = (b[1] - lat) * ky;
            const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy;
            const t = L2 > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / L2)) : 0;
            const px = ax + t * dx, py = ay + t * dy, d2 = px * px + py * py;
            if (d2 <= bestD2) {
                bestD2 = d2;
                best = { reach: Number(id), km: a[2] + t * (b[2] - a[2]), distKm: Math.sqrt(d2), lon: lon + px / kx, lat: lat + py / ky };
            }
        }
    }
    return best;
}
export function riverPlace(graph, points, riverKm, lon, lat, maxKm) {
    const s = snap(points, lon, lat, maxKm);
    if (!s)
        return null;
    const on = alongRiverKm(riverKm, lon, lat, maxKm);
    const byLine = on != null && on.distKm <= s.distKm;
    const t = trace(graph, byLine ? on.reach : s.id);
    const km = byLine ? on.km : t.path.reduce((a, id) => a + (graph[String(id)]?.[1] ?? 0), 0);
    return { mouth: t.path[t.path.length - 1], outlet: t.outlet, km, byLine };
}
/**
 * Trace downstream from `startId` via NEXT_DOWN until the terminal (next_down 0
 * or outside the graph) or `destId`. Returns the reach id path and the outlet
 * label of the start reach.
 */
export function trace(graph, startId, destId) {
    const path = [];
    const seen = new Set();
    let cur = startId;
    let reachedDest = false;
    while (graph[String(cur)]) {
        if (seen.has(cur))
            break; // cycle guard (shouldn't happen in a DAG)
        seen.add(cur);
        path.push(cur);
        if (destId !== undefined && cur === destId) {
            reachedDest = true;
            break;
        }
        const next = graph[String(cur)][0];
        if (next === 0)
            break;
        cur = next;
    }
    const startTuple = graph[String(startId)];
    const outlet = startTuple ? startTuple[4] : "other";
    return { path, outlet, reachedDest };
}
function velocity(qCms, a) {
    const v = a * Math.pow(Math.max(qCms, 0.01), VELOCITY.b);
    return Math.min(VELOCITY.vMax, Math.max(VELOCITY.vMin, v));
}
/** Cumulative travel time (seconds) along a path for a given velocity coefficient. */
function travelSeconds(graph, path, a) {
    let seconds = 0;
    for (const id of path) {
        const t = graph[String(id)];
        if (!t)
            continue;
        const lengthM = t[1] * 1000;
        const q = t[2];
        seconds += lengthM / velocity(q, a);
    }
    return seconds;
}
export const OUTLET_NAME_TH = {
    gulf: "อ่าวไทย",
    mekong: "แม่น้ำโขง",
    andaman: "ทะเลอันดามัน",
    salween: "แม่น้ำสาละวิน",
    other: "แอ่งปิด/อื่น ๆ",
};
/** Full route from a snapped reach: trace + ETA range + flow summary. */
export function route(graph, snapped, destId) {
    const { path, outlet, reachedDest } = trace(graph, snapped.id, destId);
    const lengthKm = path.reduce((s, id) => s + (graph[String(id)]?.[1] ?? 0), 0);
    const secFast = travelSeconds(graph, path, VELOCITY.aFast);
    const secSlow = travelSeconds(graph, path, VELOCITY.aSlow);
    const startTuple = graph[String(snapped.id)];
    const endId = path[path.length - 1];
    const endTuple = endId !== undefined ? graph[String(endId)] : undefined;
    return {
        snapped,
        outlet,
        reachesGulf: outlet === GULF,
        destinationName: OUTLET_NAME_TH[outlet] ?? outlet,
        path,
        lengthKm: round(lengthKm, 1),
        etaDaysFast: round(secFast / 86400, 2),
        etaDaysSlow: round(secSlow / 86400, 2),
        qStartCms: round(startTuple?.[2] ?? 0, 2),
        qEndCms: round(endTuple?.[2] ?? 0, 2),
        reachedDest,
    };
}
function round(n, d) {
    const f = 10 ** d;
    return Math.round(n * f) / f;
}
//# sourceMappingURL=routing.js.map